import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { companyInfo } from '../data/translations';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  MapPin, Phone, Mail, Send, CheckCircle, 
  AlertCircle, Loader2
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactSection = () => {
  const { language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    clientType: '',
    service: '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const content = {
    fr: {
      badge: 'CONTACT',
      title: 'Parlons de votre projet',
      subtitle: 'Notre équipe est à votre disposition pour répondre à toutes vos questions.',
      form: {
        name: 'Nom complet',
        email: 'Email',
        phone: 'Téléphone',
        clientType: 'Type de client',
        clientTypes: {
          particular: 'Particulier',
          professional: 'Professionnel',
          company: 'Entreprise'
        },
        service: 'Service souhaité',
        serviceOptions: {
          construction: 'Construction Métallique',
          btp: 'Bâtiment & BTP',
          realestate: 'Gestion Immobilière',
          security: 'Sécurité',
          hvac: 'Froid & Climatisation',
          electrical: 'Électricité Bâtiment'
        },
        message: 'Description du projet',
        submit: 'Envoyer',
        sending: 'Envoi...',
        success: 'Votre message a été envoyé avec succès. Nous vous contacterons rapidement.',
        error: 'Une erreur est survenue. Veuillez réessayer.'
      },
      info: {
        phone: 'Téléphone',
        email: 'Email',
        address: 'Adresse'
      }
    },
    en: {
      badge: 'CONTACT',
      title: 'Let\'s talk about your project',
      subtitle: 'Our team is at your disposal to answer all your questions.',
      form: {
        name: 'Full name',
        email: 'Email',
        phone: 'Phone',
        clientType: 'Client type',
        clientTypes: {
          particular: 'Individual',
          professional: 'Professional',
          company: 'Company'
        },
        service: 'Desired service',
        serviceOptions: {
          construction: 'Metal Construction',
          btp: 'Building & BTP',
          realestate: 'Real Estate Management',
          security: 'Security',
          hvac: 'HVAC & Refrigeration',
          electrical: 'Building Electrical'
        },
        message: 'Project description',
        submit: 'Send',
        sending: 'Sending...',
        success: 'Your message has been sent successfully. We will contact you shortly.',
        error: 'An error occurred. Please try again.'
      },
      info: {
        phone: 'Phone',
        email: 'Email',
        address: 'Address'
      }
    }
  };

  const t = content[language];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await axios.post(`${BACKEND_URL}/api/quotes`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        client_type: formData.clientType,
        service: formData.service,
        message: formData.message,
        language: language
      });
      
      if (response.data.success) {
        setStatus({ type: 'success', message: t.form.success });
        setFormData({
          name: '',
          email: '',
          phone: '',
          clientType: '',
          service: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting quote:', error);
      setStatus({ type: 'error', message: t.form.error });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#C9A227]/10 border border-[#C9A227]/20 rounded-full mb-6">
            <span className="text-[#C9A227] font-semibold text-sm">{t.badge}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#1A1A1A] mb-4">
            {t.title}
          </h2>
          <p className="text-xl text-gray-600">{t.subtitle}</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Contact Info */}
          <div className="space-y-6">
            {/* Phone */}
            <div className="flex items-start gap-4 p-6 bg-gray-50 rounded-xl">
              <div className="w-12 h-12 bg-[#C9A227] rounded-xl flex items-center justify-center flex-shrink-0">
                <Phone className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-[#1A1A1A] mb-2">{t.info.phone}</h3>
                <div className="space-y-1">
                  {companyInfo.phones.map((phone, index) => (
                    <a
                      key={index}
                      href={`tel:${phone.replace(/\s/g, '')}`}
                      className="block text-gray-600 hover:text-[#C9A227] transition-colors text-sm"
                    >
                      {phone}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Email */}
            <div className="flex items-start gap-4 p-6 bg-gray-50 rounded-xl">
              <div className="w-12 h-12 bg-[#C9A227] rounded-xl flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-[#1A1A1A] mb-2">{t.info.email}</h3>
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="text-gray-600 hover:text-[#C9A227] transition-colors text-sm"
                >
                  {companyInfo.email}
                </a>
              </div>
            </div>

            {/* Address */}
            <div className="flex items-start gap-4 p-6 bg-gray-50 rounded-xl">
              <div className="w-12 h-12 bg-[#C9A227] rounded-xl flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-[#1A1A1A] mb-2">{t.info.address}</h3>
                <p className="text-gray-600 text-sm">{companyInfo.address}</p>
                <p className="text-[#C9A227] font-medium text-sm mt-1">{companyInfo.country}</p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-gray-50 rounded-2xl p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Name */}
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-[#1A1A1A]">{t.form.name} *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="border-gray-200 focus:border-[#C9A227] focus:ring-[#C9A227]"
                    />
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-[#1A1A1A]">{t.form.email} *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="border-gray-200 focus:border-[#C9A227] focus:ring-[#C9A227]"
                    />
                  </div>

                  {/* Phone */}
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-[#1A1A1A]">{t.form.phone} *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="border-gray-200 focus:border-[#C9A227] focus:ring-[#C9A227]"
                    />
                  </div>

                  {/* Client Type */}
                  <div className="space-y-2">
                    <Label className="text-[#1A1A1A]">{t.form.clientType} *</Label>
                    <Select
                      value={formData.clientType}
                      onValueChange={(value) => handleSelectChange('clientType', value)}
                    >
                      <SelectTrigger className="border-gray-200 focus:border-[#C9A227] focus:ring-[#C9A227]">
                        <SelectValue placeholder="..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="particular">{t.form.clientTypes.particular}</SelectItem>
                        <SelectItem value="professional">{t.form.clientTypes.professional}</SelectItem>
                        <SelectItem value="company">{t.form.clientTypes.company}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Service */}
                <div className="space-y-2">
                  <Label className="text-[#1A1A1A]">{t.form.service} *</Label>
                  <Select
                    value={formData.service}
                    onValueChange={(value) => handleSelectChange('service', value)}
                  >
                    <SelectTrigger className="border-gray-200 focus:border-[#C9A227] focus:ring-[#C9A227]">
                      <SelectValue placeholder="..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="construction">{t.form.serviceOptions.construction}</SelectItem>
                      <SelectItem value="btp">{t.form.serviceOptions.btp}</SelectItem>
                      <SelectItem value="realestate">{t.form.serviceOptions.realestate}</SelectItem>
                      <SelectItem value="security">{t.form.serviceOptions.security}</SelectItem>
                      <SelectItem value="hvac">{t.form.serviceOptions.hvac}</SelectItem>
                      <SelectItem value="electrical">{t.form.serviceOptions.electrical}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-[#1A1A1A]">{t.form.message} *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="border-gray-200 focus:border-[#C9A227] focus:ring-[#C9A227] resize-none"
                  />
                </div>

                {/* Status Message */}
                {status.message && (
                  <div className={`flex items-center gap-3 p-4 rounded-lg ${
                    status.type === 'success' 
                      ? 'bg-green-50 text-green-700 border border-green-200' 
                      : 'bg-red-50 text-red-700 border border-red-200'
                  }`}>
                    {status.type === 'success' 
                      ? <CheckCircle className="w-5 h-5" />
                      : <AlertCircle className="w-5 h-5" />
                    }
                    <span>{status.message}</span>
                  </div>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#C9A227] hover:bg-[#B8922A] text-white py-6 text-lg transition-all duration-300"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      {t.form.sending}
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      {t.form.submit}
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;